import React from 'react';
import { Link, graphql } from 'gatsby';

import Layout from '../components/Layout';
import SEO from '../components/seo';
import Authors from '../components/Authors';
import { categoryPermalink } from '../utils/categories';
import { stripEmojis, wrapEmojisInHtml, wrapTablesInHtml } from '../utils/emoji';

import './blog-post.css';

function readingTimeMinutes(html) {
  if (!html || typeof html !== 'string') return 1;
  const text = html.replace(/<[^>]+>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

class BlogPostTemplate extends React.Component {
  render() {
    const post = this.props.data.markdownRemark;
    const { previous, next } = this.props.pageContext;
    const { title, date, categories, authors, image } = post.frontmatter;
    const ogImage =
      image &&
      image.childImageSharp &&
      image.childImageSharp.resize;
    const imageSrc = ogImage && ogImage.src;
    const minutes = readingTimeMinutes(post.html);
    const displayTitle = stripEmojis(title);
    const articleHtml = wrapTablesInHtml(wrapEmojisInHtml(post.html));

    return (
      <Layout className="blogPost">
        <SEO
          title={displayTitle}
          description={post.fields.teaserPlainText || post.excerpt}
          image={imageSrc}
          imageWidth={ogImage && ogImage.width}
          imageHeight={ogImage && ogImage.height}
          pathname={post.fields.slug}
          type="article"
        />

        <div className="blogPost__grid">
          <article className="blogPost__article">
            <header className="blogPost__header">
              <h1 className="blogPost__title">{displayTitle}</h1>

              <p className="blogPost__metadata blogPost__metadataDesktop">
                <span>{date}</span>
                {authors && authors.length > 0 && (
                  <React.Fragment>
                    <span className="blogPost__metadataSep" aria-hidden="true">
                      •
                    </span>
                    <span>
                      by <Authors authors={[...authors]} />
                    </span>
                  </React.Fragment>
                )}
                <span className="blogPost__metadataSep" aria-hidden="true">
                  •
                </span>
                <span>
                  {minutes} minute{minutes === 1 ? '' : 's'} read
                </span>
              </p>

              <div className="blogPost__metadataMobile">
                {authors && authors.length > 0 && (
                  <span>
                    by <Authors authors={[...authors]} />
                  </span>
                )}
                <div className="blogPost__metadataMobileDetails">
                  <span>{date}</span>
                  <span className="blogPost__metadataSep" aria-hidden="true">
                    •
                  </span>
                  <span>
                    {minutes} minute{minutes === 1 ? '' : 's'} read
                  </span>
                </div>
              </div>

              {categories && categories.length > 0 && (
                <ul className="blogPost__tags">
                  {categories.map(category => (
                    <li key={category}>
                      <Link
                        to={categoryPermalink(category)}
                        className="blogPost__tag"
                      >
                        {category}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </header>

            <div
              className="blogPost__content"
              dangerouslySetInnerHTML={{ __html: articleHtml }}
            />
          </article>

          <aside className="blogPost__sidebar">
            <Link to="/" className="blogPost__back">
              ← back
            </Link>

            <div className="blogPost__adjacent">
              {previous && (
                <div className="blogPost__adjacentBlock">
                  <p className="blogPost__adjacentLabel">Previous article</p>
                  <Link
                    to={previous.fields.slug}
                    rel="prev"
                    className="blogPost__adjacentTitle"
                  >
                    {previous.frontmatter.title}
                  </Link>
                </div>
              )}
              {next && (
                <div className="blogPost__adjacentBlock">
                  <p className="blogPost__adjacentLabel">Next article</p>
                  <Link
                    to={next.fields.slug}
                    rel="next"
                    className="blogPost__adjacentTitle"
                  >
                    {next.frontmatter.title}
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      </Layout>
    );
  }
}

export default BlogPostTemplate;

export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    site {
      siteMetadata {
        title
        author
      }
    }
    markdownRemark(fields: { slug: { eq: $slug } }) {
      id
      excerpt(format: PLAIN, pruneLength: 500)
      html
      fields {
        slug
        teaserPlainText
      }
      frontmatter {
        title
        date(formatString: "MMM Do, YYYY")
        categories
        authors {
          name
          link
        }
        image {
          childImageSharp {
            resize(width: 1200, height: 630, cropFocus: CENTER) {
              src
              width
              height
            }
          }
        }
      }
    }
  }
`;
